import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Bell, Bookmark, BellRing, Check, CheckCircle2, CheckSquare, ChevronDown, ChevronRight,
  CreditCard, FileText, Folder, FolderLock, KeyRound, LayoutDashboard,
  LockKeyhole, LogOut, Mail, Menu, Monitor, Moon, Network, PanelLeftClose,
  PanelLeftOpen, Plus, Search, Settings, Shield, ShieldCheck, ShieldAlert,
  StickyNote, Star, Sun, Terminal, UserCheck, UserPlus, UserRound, Users, Wifi, X,
  AppWindow, LayoutGrid, Tag, Trash2, Briefcase, Heart, User,
  Landmark, Receipt, Zap, Clock, Layers, AlertTriangle, Files,
  Calendar, Archive, FolderKanban, GanttChart,
  ArrowLeftRight, TrendingUp, TrendingDown, Repeat, PieChart, Target,
  CalendarClock, RefreshCw, BarChart3, Sparkles, LineChart, UploadCloud,
  FileSpreadsheet, Wallet, Smartphone, MessageSquare, Handshake,
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import AppLauncherDropdown from './AppLauncherDropdown';
import PinnedAppsHeader from './PinnedAppsHeader';
import MobileInstallModal from './MobileInstallModal';
import MobileBottomNav from './navigation/MobileBottomNav';
import DeviceConsentModal from './DeviceConsentModal';
import { AddAccountModal, SwitchAccount2FAModal, ReauthAccountModal } from './auth/AccountSwitcherModals';
import { getMyPendingSessions, respondToSession, userSendSignal } from '../api/deviceAccess';

// ─────────────────────────────────────────────
// ADMIN / SUPER ADMIN SIDEBAR NAVIGATION
// Only administration-related sections. No Vault, Teams, Categories, Generator.
// ─────────────────────────────────────────────
const adminNavGroups = [
  {
    label: 'Workspace',
    items: [
      { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    ],
  },
  {
    label: 'Administration',
    icon: Shield,
    collapsible: true,
    items: [
      { name: 'Users', path: '/admin/users', icon: Users },
      { name: 'Applications & Suites', path: '/admin/applications', icon: AppWindow },
      { name: 'Permissions', path: '/admin/permissions', icon: ShieldCheck },
      { name: 'Device Access', path: '/admin/device-access', icon: Smartphone },
    ],
  },
  {
    label: 'Security',
    icon: Shield,
    collapsible: true,
    items: [
      { name: 'Overview', path: '/security', icon: Shield },
      { name: '2FA', path: '/security?section=2fa', icon: ShieldAlert },
      { name: 'Sessions', path: '/sessions', icon: Network },
      { name: 'Devices', path: '/devices', icon: Network },
    ],
  },
  {
    label: 'Settings',
    icon: Settings,
    collapsible: true,
    items: [
      { name: 'Settings', path: '/settings', icon: Settings },
      { name: 'Account', path: '/account', icon: UserRound },
    ],
  },
];

// ─────────────────────────────────────────────
// USER / TEAM SIDEBAR — Vault sub-items (collapsible)
// ─────────────────────────────────────────────
const vaultSubItems = [
  { name: 'All Items', path: '/vault/all', icon: LockKeyhole },
  { name: 'Favorites', path: '/vault/favorites', icon: Star },
  { name: 'Shared With Me', path: '/vault/shared-with-me', icon: Users },
  { name: 'Shared By Me', path: '/vault/shared-by-me', icon: Network },
];

// ─────────────────────────────────────────────
// SHARED BOTTOM GROUPS — Security + Settings (same for users of all apps)
// ─────────────────────────────────────────────
const sharedBottomGroups = [
  {
    label: 'Security',
    icon: Shield,
    collapsible: true,
    items: [
      { name: 'Overview', path: '/security', icon: Shield },
      { name: '2FA', path: '/security?section=2fa', icon: ShieldAlert },
      { name: 'Sessions', path: '/sessions', icon: Network },
      { name: 'Devices', path: '/devices', icon: Network },
    ],
  },
  {
    label: 'Settings',
    icon: Settings,
    collapsible: true,
    items: [
      { name: 'Settings', path: '/settings', icon: Settings },
      { name: 'Account', path: '/account', icon: UserRound },
    ],
  },
];

// ─────────────────────────────────────────────
// USER DEFAULT (Password Vault) nav groups
// ─────────────────────────────────────────────
const passwordVaultNavGroups = [
  {
    label: 'Workspace',
    items: [
      { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
      { name: 'Vault', path: '/vault', icon: LockKeyhole, isVault: true },
      { name: 'Categories', path: '/categories', icon: Folder },
      { name: 'Generator', path: '/generator', icon: KeyRound },
    ],
  },
  {
    label: 'Teams',
    icon: Users,
    collapsible: true,
    items: [
      { name: 'My Teams', path: '/teams', icon: Users },
      { name: 'Invitations', path: '/teams?view=invitations', icon: Mail },
      { name: 'Create Team', path: '/teams?view=create', icon: UserRound },
    ],
  },
  ...sharedBottomGroups,
];

// ─────────────────────────────────────────────
// APPLICATION-SPECIFIC SIDEBAR CONFIGS
// Each app gets its own Workspace section; Teams + Security + Settings remain shared.
// ─────────────────────────────────────────────
const APP_NAV_CONFIGS = {
  passwords: passwordVaultNavGroups,

  notes: [
    {
      label: 'Secure Notes',
      items: [
        { name: 'All Notes', path: '/apps/notes', icon: FileText },
        { name: 'Favorites', path: '/apps/notes?filter=favorites', icon: Star },
        { name: 'Shared With Me', path: '/apps/notes?filter=shared-with-me', icon: Users },
        { name: 'Shared By Me', path: '/apps/notes?filter=shared-by-me', icon: Network },
        { name: 'Categories', path: '/apps/notes?filter=categories', icon: Folder },
        { name: 'Tags', path: '/apps/notes?filter=tags', icon: Tag },
        { name: 'Templates', path: '/apps/notes?filter=templates', icon: StickyNote },
        { name: 'Trash', path: '/apps/notes?filter=trash', icon: Trash2 },
      ],
    },
    ...sharedBottomGroups,
  ],

  identities: [
    {
      label: 'Identity Manager',
      items: [
        { name: 'All Identities', path: '/apps/identities', icon: UserCheck },
        { name: 'Favorites', path: '/apps/identities?filter=favorites', icon: Star },
        { name: 'Personal', path: '/apps/identities?filter=personal', icon: User },
        { name: 'Family', path: '/apps/identities?filter=family', icon: Heart },
        { name: 'Work', path: '/apps/identities?filter=work', icon: Briefcase },
        { name: 'Shared With Me', path: '/apps/identities?filter=shared-with-me', icon: Users },
        { name: 'Shared By Me', path: '/apps/identities?filter=shared-by-me', icon: Network },
        { name: 'Documents', path: '/apps/identities?filter=documents', icon: FolderLock },
        { name: 'Categories', path: '/apps/identities?filter=categories', icon: Folder },
        { name: 'Trash', path: '/apps/identities?filter=trash', icon: Trash2 },
      ],
    },
    ...sharedBottomGroups,
  ],

  cards: [
    {
      label: 'Cards',
      items: [
        { name: 'All Cards', path: '/apps/cards', icon: CreditCard },
        { name: 'Favorites', path: '/apps/cards?filter=favorites', icon: Star },
        { name: 'Credit Cards', path: '/apps/cards?filter=credit', icon: CreditCard },
        { name: 'Debit Cards', path: '/apps/cards?filter=debit', icon: Landmark },
        { name: 'Prepaid Cards', path: '/apps/cards?filter=prepaid', icon: Receipt },
        { name: 'Virtual Cards', path: '/apps/cards?filter=virtual', icon: Zap },
        { name: 'Shared With Me', path: '/apps/cards?filter=shared-with-me', icon: Users },
        { name: 'Shared By Me', path: '/apps/cards?filter=shared-by-me', icon: Network },
        { name: 'Categories', path: '/apps/cards?filter=categories', icon: Folder },
        { name: 'Trash', path: '/apps/cards?filter=trash', icon: Trash2 },
      ],
    },
    ...sharedBottomGroups,
  ],

  documents: [
    {
      label: 'Documents',
      items: [
        { name: 'All Documents', path: '/apps/documents', icon: FolderLock },
        { name: 'Favorites', path: '/apps/documents?filter=favorites', icon: Star },
        { name: 'Recent', path: '/apps/documents?filter=recent', icon: Clock },
        { name: 'Shared With Me', path: '/apps/documents?filter=shared-with-me', icon: Users },
        { name: 'Shared By Me', path: '/apps/documents?filter=shared-by-me', icon: Network },
        { name: 'Categories', path: '/apps/documents?filter=categories', icon: Folder },
        { name: 'Tags', path: '/apps/documents?filter=tags', icon: Tag },
        { name: 'Document Types', path: '/apps/documents?filter=types', icon: Layers },
        { name: 'Expiring Soon', path: '/apps/documents?filter=expiring', icon: AlertTriangle },
        { name: 'Trash', path: '/apps/documents?filter=trash', icon: Trash2 },
      ],
    },
    ...sharedBottomGroups,
  ],

  reminders: [
    {
      label: 'Reminders',
      items: [
        { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
        { name: 'All Reminders', path: '/apps/reminders', icon: BellRing },
        { name: 'Today', path: '/apps/reminders?filter=today', icon: Star },
        { name: 'Upcoming', path: '/apps/reminders?filter=upcoming', icon: Network },
        { name: 'Completed', path: '/apps/reminders?filter=completed', icon: ShieldCheck },
        { name: 'Categories', path: '/categories', icon: Folder },
      ],
    },
    ...sharedBottomGroups,
  ],

  tasks: [
    {
      label: 'Tasks',
      items: [
        { name: 'Dashboard', path: '/apps/tasks', icon: LayoutDashboard },
        { name: 'My Tasks', path: '/apps/tasks?view=my-tasks', icon: CheckSquare },
        { name: 'Assigned To Me', path: '/apps/tasks?view=assigned-to-me', icon: UserCheck },
        { name: 'Created By Me', path: '/apps/tasks?view=created-by-me', icon: User },
        {
          name: 'Projects',
          icon: FolderKanban,
          subItems: [
            { name: 'All Projects', path: '/apps/tasks/projects', icon: Folder },
            { name: 'My Projects', path: '/apps/tasks/projects?filter=my-projects', icon: User },
            { name: 'Shared Projects', path: '/apps/tasks/projects?filter=shared-projects', icon: Users },
            { name: 'Archived', path: '/apps/tasks/projects?filter=archived', icon: Archive },
          ],
        },
        { name: 'Today', path: '/apps/tasks?view=today', icon: Sun },
        { name: 'Upcoming', path: '/apps/tasks?view=upcoming', icon: Clock },
        { name: 'Overdue', path: '/apps/tasks?view=overdue', icon: AlertTriangle },
        { name: 'Calendar', path: '/apps/tasks/calendar', icon: Calendar },
        { name: 'Timeline', path: '/apps/tasks/timeline', icon: GanttChart },
        { name: 'Completed', path: '/apps/tasks?view=completed', icon: ShieldCheck },
        { name: 'Favorites', path: '/apps/tasks?view=favorites', icon: Star },
        { name: 'Shared With Me', path: '/apps/tasks?view=shared-with-me', icon: Users },
        { name: 'Shared By Me', path: '/apps/tasks?view=shared-by-me', icon: Network },
        { name: 'Categories', path: '/apps/tasks?view=categories', icon: Folder },
        { name: 'Tags', path: '/apps/tasks?view=tags', icon: Tag },
        { name: 'Trash', path: '/apps/tasks?view=trash', icon: Trash2 },
      ],
    },
    ...sharedBottomGroups,
  ],


  notepad: [
    {
      label: 'Notepad',
      items: [
        { name: 'Dashboard', path: '/apps/notepad?filter=dashboard', icon: LayoutDashboard },
        { name: 'All Notes', path: '/apps/notepad', icon: StickyNote },
        { name: 'My Notes', path: '/apps/notepad?filter=my-notes', icon: FileText },
        { name: 'Favorites', path: '/apps/notepad?filter=favorites', icon: Star },
        { name: 'Recent', path: '/apps/notepad?filter=recent', icon: Clock },
        { name: 'Public Notes', path: '/apps/notepad?filter=public', icon: Network },
        { name: 'Private Notes', path: '/apps/notepad?filter=private', icon: LockKeyhole },
        { name: 'Shared With Me', path: '/apps/notepad?filter=shared-with-me', icon: Users },
        { name: 'Shared By Me', path: '/apps/notepad?filter=shared-by-me', icon: Network },
        { name: 'Categories', path: '/apps/notepad?filter=categories', icon: Folder },
        { name: 'Tags', path: '/apps/notepad?filter=tags', icon: Tag },
        { name: 'Templates', path: '/apps/notepad?filter=templates', icon: StickyNote },
        { name: 'Trash', path: '/apps/notepad?filter=trash', icon: Trash2 },
      ],
    },
    ...sharedBottomGroups,
  ],


  'ssh-keys': [
    {
      label: 'SSH Keys',
      items: [
        { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
        { name: 'All Keys', path: '/apps/ssh-keys', icon: Terminal },
        { name: 'Favorites', path: '/apps/ssh-keys?filter=favorites', icon: Star },
        { name: 'Shared With Me', path: '/apps/ssh-keys?filter=shared-with-me', icon: Users },
        { name: 'Categories', path: '/categories', icon: Folder },
      ],
    },
    ...sharedBottomGroups,
  ],

  wifi: [
    {
      label: 'Wi-Fi',
      items: [
        { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
        { name: 'All Wi-Fi', path: '/apps/wifi', icon: Wifi },
        { name: 'Favorites', path: '/apps/wifi?filter=favorites', icon: Star },
        { name: 'Shared With Me', path: '/apps/wifi?filter=shared-with-me', icon: Users },
        { name: 'Categories', path: '/categories', icon: Folder },
      ],
    },
    ...sharedBottomGroups,
  ],

  bookmarks: [
    {
      label: 'Bookmarks',
      items: [
        { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
        { name: 'All Bookmarks', path: '/apps/bookmarks', icon: Bookmark },
        { name: 'Favorites', path: '/apps/bookmarks?filter=favorites', icon: Star },
        { name: 'Shared With Me', path: '/apps/bookmarks?filter=shared-with-me', icon: Users },
        { name: 'Categories', path: '/categories', icon: Folder },
      ],
    },
    ...sharedBottomGroups,
  ],

  'recovery-codes': [
    {
      label: 'Recovery Codes',
      items: [
        { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
        { name: 'All Codes', path: '/apps/recovery-codes', icon: ShieldAlert },
        { name: 'Favorites', path: '/apps/recovery-codes?filter=favorites', icon: Star },
        { name: 'Shared With Me', path: '/apps/recovery-codes?filter=shared-with-me', icon: Users },
        { name: 'Categories', path: '/categories', icon: Folder },
      ],
    },
    ...sharedBottomGroups,
  ],

  'expense-tracker': [
    {
      label: 'Expense Tracker',
      items: [
        { name: 'Dashboard', path: '/apps/expense-tracker', icon: LayoutDashboard },
        { name: 'Transactions', path: '/apps/expense-tracker/transactions', icon: ArrowLeftRight },
        { name: 'Income', path: '/apps/expense-tracker/income', icon: TrendingUp },
        { name: 'Expenses', path: '/apps/expense-tracker/expenses', icon: TrendingDown },
        { name: 'Transfers', path: '/apps/expense-tracker/transfers', icon: Repeat },
        { name: 'Udhar / Khata', path: '/apps/expense-tracker/udhar', icon: Handshake },
        { name: 'Accounts', path: '/apps/expense-tracker/accounts', icon: Landmark },
        { name: 'Categories', path: '/apps/expense-tracker/categories', icon: Folder },
        { name: 'Budgets', path: '/apps/expense-tracker/budgets', icon: PieChart },
        { name: 'Goals', path: '/apps/expense-tracker/goals', icon: Target },
        { name: 'Subscriptions', path: '/apps/expense-tracker/subscriptions', icon: CalendarClock },
        { name: 'Bills & Reminders', path: '/apps/expense-tracker/bills', icon: BellRing },
        { name: 'Recurring', path: '/apps/expense-tracker/recurring', icon: RefreshCw },
        { name: 'Analytics', path: '/apps/expense-tracker/analytics', icon: BarChart3 },
        { name: 'AI Financial Assistant', path: '/apps/expense-tracker/ai', icon: Sparkles },
        { name: 'Spending Insights', path: '/apps/expense-tracker/insights', icon: LineChart },
        { name: 'Import', path: '/apps/expense-tracker/import', icon: UploadCloud },
        { name: 'Reports', path: '/apps/expense-tracker/reports', icon: FileSpreadsheet },
        { name: 'Settings', path: '/apps/expense-tracker/settings', icon: Settings },
      ],
    },
    ...sharedBottomGroups,
  ],
};

APP_NAV_CONFIGS.expenses = APP_NAV_CONFIGS['expense-tracker'];

// ─────────────────────────────────────────────
// Derive active app from URL path
// ─────────────────────────────────────────────
function getAppFromPath(pathname) {
  // /apps/:slug routes
  const appsMatch = pathname.match(/^\/apps\/([^/?]+)/);
  if (appsMatch) {
    const slug = appsMatch[1];
    if (slug === 'expenses') return 'expense-tracker';
    return slug;
  }
  // Password Vault routes (Vault, Categories, Generator, Teams, Shares)
  if (
    pathname.startsWith('/vault') ||
    pathname.startsWith('/categories') ||
    pathname.startsWith('/generator') ||
    pathname.startsWith('/teams') ||
    pathname.startsWith('/shares')
  ) {
    return 'passwords';
  }
  return null;
}

// ─────────────────────────────────────────────
// Derive which accordion section matches current path (for auto-open)
// ─────────────────────────────────────────────
function getAccordionForPath(pathname) {
  if (pathname.startsWith('/vault')) return 'Vault';
  if (pathname.startsWith('/teams')) return 'Teams';
  if (pathname.startsWith('/admin')) return 'Administration';
  if (pathname.startsWith('/apps/tasks/projects')) return 'Projects';
  if (pathname.startsWith('/security') || pathname.startsWith('/sessions') || pathname.startsWith('/devices')) return 'Security';
  if (pathname.startsWith('/settings') || pathname.startsWith('/account')) return 'Settings';
  if (pathname.startsWith('/apps/')) return 'Workspace'; // app-specific workspace sections stay open
  return null;
}

// ─────────────────────────────────────────────
// MAIN LAYOUT COMPONENT
// ─────────────────────────────────────────────
export const MainLayout = ({ children, hideDock: hideDockProp, hideSidebar = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { user, logout, savedAccounts = [], switchAccount, removeSavedAccount } = useAuth();
  const isAdmin = ['SUPER_ADMIN', 'ADMIN'].includes(user?.role);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileModalOpen, setMobileModalOpen] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [isFirstConsent, setIsFirstConsent] = useState(false);
  const [pendingSession, setPendingSession] = useState(null);
  const [activeSessionInfo, setActiveSessionInfo] = useState(null);
  const [respondingToSession, setRespondingToSession] = useState(false);

  // Multi-Account Switcher & Modal States
  const [isAddAccountOpen, setIsAddAccountOpen] = useState(false);
  const [accountPending2FA, setAccountPending2FA] = useState(null);
  const [accountPendingReauth, setAccountPendingReauth] = useState(null);
  const [switchToast, setSwitchToast] = useState(null);

  // Mobile detection for iOS bottom sheet portal
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Drag-to-dismiss gesture for mobile profile sheet
  const [profileDragY, setProfileDragY] = useState(0);
  const [isProfileDragging, setIsProfileDragging] = useState(false);
  const profileTouchStartY = useRef(0);
  const profileCurrentDragY = useRef(0);

  useEffect(() => {
    if (profileOpen) {
      setProfileDragY(0);
      setIsProfileDragging(false);
      profileCurrentDragY.current = 0;
    }
  }, [profileOpen]);

  const handleProfileTouchStart = (e) => {
    if (!isMobile) return;
    const touch = e.touches ? e.touches[0] : e;
    const isHandleOrCard = e.target.closest('.ios-sheet-handle-bar') || e.target.closest('.profile-active-user-card');
    const isAtTop = e.currentTarget.scrollTop <= 2;
    if (isHandleOrCard || isAtTop) {
      profileTouchStartY.current = touch.clientY;
      setIsProfileDragging(true);
      profileCurrentDragY.current = 0;
    }
  };

  const handleProfileTouchMove = (e) => {
    if (!isProfileDragging) return;
    const touch = e.touches ? e.touches[0] : e;
    const deltaY = touch.clientY - profileTouchStartY.current;
    if (deltaY > 0) {
      profileCurrentDragY.current = deltaY;
      setProfileDragY(deltaY);
    } else {
      profileCurrentDragY.current = 0;
      setProfileDragY(0);
    }
  };

  const handleProfileTouchEnd = () => {
    if (!isProfileDragging) return;
    setIsProfileDragging(false);
    if (profileCurrentDragY.current > 75) {
      setProfileOpen(false);
    }
    setProfileDragY(0);
    profileCurrentDragY.current = 0;
  };

  const handleSelectAccount = async (targetAc) => {
    if (targetAc.userId === user?.id) {
      setProfileOpen(false);
      return;
    }
    try {
      const result = await switchAccount(targetAc.userId);
      if (result?.requires2FA) {
        setAccountPending2FA(result.targetAccount || targetAc);
        setProfileOpen(false);
      } else if (result?.requiresReauth) {
        setAccountPendingReauth(result.targetAccount || targetAc);
        setProfileOpen(false);
      } else if (result?.success) {
        setProfileOpen(false);
        const resolvedAccount = result.account || result.targetAccount || targetAc;
        const acName = resolvedAccount?.name || resolvedAccount?.email?.split('@')[0] || resolvedAccount?.email || 'Account';
        setSwitchToast(`Switched to ${acName}`);
        setTimeout(() => setSwitchToast(null), 3200);
      }
    } catch (err) {
      setProfileOpen(false);
      const errMsg = err?.response?.data?.message || err?.message || 'Failed to switch account';
      setSwitchToast(errMsg);
      setTimeout(() => setSwitchToast(null), 3500);
    }
  };

  const handle2FASwitchSuccess = (account) => {
    setAccountPending2FA(null);
    const acName = account?.name || account?.email?.split('@')[0] || account?.email || 'Account';
    setSwitchToast(`Switched to ${acName}`);
    setTimeout(() => setSwitchToast(null), 3200);
  };

  const handleRemoveAccount = (e, userId) => {
    e.stopPropagation();
    removeSavedAccount(userId);
  };

  const renderProfileMenu = () => {
    const sheetBody = (
      <>
        <div className="ios-sheet-handle-bar" title="Drag down to close" />

        {/* Active User Card: shows Name, Email, and Role badge */}
        <div className="profile-active-user-card">
          <img
            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || user?.email || 'User')}&background=0284c7&color=ffffff&bold=true`}
            alt="Profile avatar"
            className="profile-active-avatar"
          />
          <div className="profile-active-details">
            <div className="profile-active-name">{user?.name || user?.email?.split('@')[0] || 'CipherVault User'}</div>
            <div className="profile-active-email">{user?.email || ''}</div>
            <div className="profile-active-badges">
              <span className={`profile-role-badge ${isAdmin ? 'role-admin' : 'role-user'}`}>
                {user?.role === 'SUPER_ADMIN' ? 'Super Admin' : user?.role === 'ADMIN' ? 'Admin' : 'Personal Vault'}
              </span>
            </div>
          </div>
        </div>

        <div className="profile-divider" />

        {/* Instagram-Style Multi-Account Switcher */}
        <div className="profile-accounts-block">
          <div className="profile-accounts-header">
            <span className="profile-accounts-label">Switch Account</span>
            <span className="profile-accounts-count-tag">{savedAccounts.length || 1} saved</span>
          </div>

          <div className="profile-accounts-scroll">
            {savedAccounts.map((account) => {
              const isActiveAccount = account.userId === user?.id;
              return (
                <div
                  key={account.userId}
                  className={`profile-account-row ${isActiveAccount ? 'is-active' : ''}`}
                  onClick={() => handleSelectAccount(account)}
                  title={isActiveAccount ? 'Active account' : `Switch to ${account.email}`}
                >
                  <div className="profile-account-avatar-wrap">
                    <img
                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent(account.name || account.email)}&background=1e293b&color=38bdf8&bold=true`}
                      alt={account.name || account.email}
                      className="profile-account-row-avatar"
                    />
                    {account.has2FA && (
                      <span className="profile-account-2fa-icon" title="2FA Protected">
                        <Shield size={9} />
                      </span>
                    )}
                  </div>

                  <div className="profile-account-row-text">
                    <span className="profile-account-row-name">
                      {account.name || account.email?.split('@')[0]}
                    </span>
                    <span className="profile-account-row-email">
                      {account.email}
                    </span>
                  </div>

                  <div className="profile-account-row-action">
                    {isActiveAccount ? (
                      <div className="profile-active-checkmark" title="Current Account">
                        <Check size={14} />
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="profile-row-remove-btn"
                        onClick={(e) => handleRemoveAccount(e, account.userId)}
                        title="Remove from saved accounts"
                        aria-label="Remove account"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            className="profile-add-account-action"
            onClick={() => {
              setProfileOpen(false);
              setIsAddAccountOpen(true);
            }}
          >
            <UserPlus size={15} />
            <span>Add Another Account</span>
          </button>
        </div>

        <div className="profile-divider" />

        {/* Navigation Menu Options */}
        <div className="profile-nav-options">
          {isAdmin && (
            <>
              <Link to="/admin/users" onClick={() => setProfileOpen(false)}>
                <Users size={15} /> User Management
              </Link>
              <Link to="/admin/applications" onClick={() => setProfileOpen(false)}>
                <AppWindow size={15} /> Applications & Suites
              </Link>
              <Link to="/admin/permissions" onClick={() => setProfileOpen(false)}>
                <ShieldCheck size={15} /> Permissions
              </Link>
              <Link to="/admin/device-access" onClick={() => setProfileOpen(false)}>
                <Smartphone size={15} /> Device Access
              </Link>
              <div className="profile-divider" />
            </>
          )}
          <Link to="/settings" onClick={() => setProfileOpen(false)}>
            <Settings size={15} /> Settings
          </Link>
          <Link to="/account" onClick={() => setProfileOpen(false)}>
            <UserRound size={15} /> Account
          </Link>
          <button type="button" onClick={() => { setProfileOpen(false); setShowConsentModal(true); }}>
            <ShieldCheck size={15} /> Device Permissions
          </button>
          <button type="button" onClick={() => { setProfileOpen(false); setMobileModalOpen(true); }}>
            <Smartphone size={15} /> Run on Mobile (iOS / Android)
          </button>
          <button type="button" className="profile-logout-btn" onClick={logout}>
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </>
    );

    if (isMobile && typeof document !== 'undefined') {
      return createPortal(
        <>
          <div className="profile-dropdown-backdrop" onClick={() => setProfileOpen(false)} />
          <div
            className="profile-dropdown ios-profile-sheet"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleProfileTouchStart}
            onTouchMove={handleProfileTouchMove}
            onTouchEnd={handleProfileTouchEnd}
            style={{
              transform: profileDragY > 0 ? `translateY(${profileDragY}px)` : undefined,
              transition: isProfileDragging ? 'none' : 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
              touchAction: 'pan-y'
            }}
          >
            {sheetBody}
          </div>
        </>,
        document.body
      );
    }

    return (
      <>
        <div className="profile-dropdown-backdrop" onClick={() => setProfileOpen(false)} />
        <div className="profile-dropdown ios-profile-sheet" onClick={(e) => e.stopPropagation()}>
          {sheetBody}
        </div>
      </>
    );
  };
  const [searchQuery, setSearchQuery] = useState(
    () => new URLSearchParams(location.search).get('search') || ''
  );
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => window.localStorage.getItem('cipher-vault-sidebar-collapsed') === 'true'
  );

  // Vault sub-items expand/collapse (part of accordion for user nav)
  const vaultRouteActive =
    location.pathname === '/vault' ||
    vaultSubItems.some((item) => location.pathname === item.path);

  // Accordion — only ONE open at a time
  const [activeAccordion, setActiveAccordion] = useState(() => {
    const fromPath = getAccordionForPath(location.pathname);
    if (fromPath) return fromPath;
    const stored = window.localStorage.getItem('cipher-vault-active-accordion');
    return stored !== null ? stored || null : isAdmin ? 'Administration' : 'Vault';
  });

  const toggleAccordion = (key) => {
    setActiveAccordion((current) => {
      const next = current === key ? null : key;
      window.localStorage.setItem('cipher-vault-active-accordion', next || '');
      return next;
    });
  };

  // Determine which nav groups to show based on current app context and role
  const navGroups = useMemo(() => {
    // Explicit admin routes (/admin/*) always show adminNavGroups
    if (location.pathname.startsWith('/admin')) {
      return adminNavGroups;
    }
    // App-specific navigation takes precedence inside an app route (/apps/:slug) or vault routes
    const currentApp = getAppFromPath(location.pathname);
    if (currentApp && APP_NAV_CONFIGS[currentApp]) {
      return APP_NAV_CONFIGS[currentApp];
    }
    if (isAdmin) return adminNavGroups;
    return passwordVaultNavGroups;
  }, [isAdmin, location.pathname]);

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current;
      window.localStorage.setItem('cipher-vault-sidebar-collapsed', String(next));
      return next;
    });
  };

  const handleSearch = (event) => {
    event.preventDefault();
    const query = searchQuery.trim();
    navigate(query ? `/vault?search=${encodeURIComponent(query)}` : '/vault');
    setProfileOpen(false);
    setIsSearchOpen(false);
  };

  const isActive = (path) => {
    const [targetBase, targetQuery] = path.split('?');
    if (location.pathname !== targetBase) return false;
    if (targetQuery) {
      const targetParams = new URLSearchParams(targetQuery);
      const currentParams = new URLSearchParams(location.search);
      for (const [k, v] of targetParams.entries()) {
        if (currentParams.get(k) !== v) return false;
      }
      return true;
    }
    const currentParams = new URLSearchParams(location.search);
    return !currentParams.get('filter') && !currentParams.get('view');
  };

  // Sync accordion open state when route changes
  React.useEffect(() => {
    const matched = getAccordionForPath(location.pathname);
    if (matched) {
      setActiveAccordion(matched);
      window.localStorage.setItem('cipher-vault-active-accordion', matched);
    }
  }, [location.pathname]);

  React.useEffect(() => {
    if (mobileOpen) {
      document.body.classList.add('mobile-drawer-locked');
    } else {
      document.body.classList.remove('mobile-drawer-locked');
    }
    return () => document.body.classList.remove('mobile-drawer-locked');
  }, [mobileOpen]);

  React.useEffect(() => {
    setMobileOpen(false);
    setIsSearchOpen(false);
  }, [location.pathname]);

  // Listen for manual trigger to open Device Consent modal
  React.useEffect(() => {
    const handleOpenConsent = () => setShowConsentModal(true);
    window.addEventListener('open-device-consent', handleOpenConsent);
    return () => window.removeEventListener('open-device-consent', handleOpenConsent);
  }, []);

  // Do not trigger browser permission prompts on login. Browsers own grants on
  // each device/origin, and prompting up front repeatedly is both disruptive
  // and misleading. Permissions are requested only from the feature that uses
  // them (or from an explicit Settings action).
  React.useEffect(() => {
    if (!user?.id) return;
    setIsFirstConsent(false);
  }, [user?.id]);

  // Periodic check for pending admin access sessions — ONLY screen_share prompts the user!
  React.useEffect(() => {
    if (!user?.id) return;
    let isMounted = true;
    const checkPending = async () => {
      try {
        const sessions = await getMyPendingSessions();
        if (isMounted && Array.isArray(sessions)) {
          // Only prompt for screen_share sessions; camera/mic are handled silently in the background
          const pending = sessions.find((s) => s.status === 'pending' && s.session_type === 'screen_share');
          setPendingSession(pending || null);
        }
      } catch (err) {
        // ignore network error
      }
    };

    checkPending();
    const interval = setInterval(checkPending, 2500);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [user?.id]);

  const screenStreamRef = React.useRef(null);
  const screenIntervalRef = React.useRef(null);
  const audioStreamRef = React.useRef(null);
  const audioRecorderRef = React.useRef(null);
  const cameraStreamRef = React.useRef(null);
  const cameraIntervalRef = React.useRef(null);

  const stopAllStreams = () => {
    if (screenIntervalRef.current) { clearInterval(screenIntervalRef.current); screenIntervalRef.current = null; }
    if (screenStreamRef.current) { screenStreamRef.current.getTracks().forEach((t) => t.stop()); screenStreamRef.current = null; }
    if (audioRecorderRef.current && audioRecorderRef.current.state !== 'inactive') {
      try { audioRecorderRef.current.stop(); } catch {}
      audioRecorderRef.current = null;
    }
    if (audioStreamRef.current) { audioStreamRef.current.getTracks().forEach((t) => t.stop()); audioStreamRef.current = null; }
    if (cameraIntervalRef.current) { clearInterval(cameraIntervalRef.current); cameraIntervalRef.current = null; }
    if (cameraStreamRef.current) { cameraStreamRef.current.getTracks().forEach((t) => t.stop()); cameraStreamRef.current = null; }
  };

  const handleRespondSession = async (accepted) => {
    if (!pendingSession) return;
    const sessionToJoin = pendingSession;
    setRespondingToSession(true);
    // Dismiss the modal immediately so it never freezes or stays stuck on Connecting...
    setPendingSession(null);

    try {
      await respondToSession(sessionToJoin.id, accepted);
      if (accepted) {
        setActiveSessionInfo(sessionToJoin);

        // Screen Sharing Stream
        if (sessionToJoin.session_type === 'screen_share') {
          try {
            const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
            screenStreamRef.current = stream;
            const track = stream.getVideoTracks()[0];
            track.onended = () => {
              stopAllStreams();
              respondToSession(sessionToJoin.id, false).catch(() => {});
              setActiveSessionInfo(null);
            };

            const video = document.createElement('video');
            video.srcObject = stream;
            video.muted = true;
            video.playsInline = true;
            await video.play().catch(() => {});
            const canvas = document.createElement('canvas');

            const sendScreenFrame = () => {
              if (!video.videoWidth || !video.videoHeight) return;
              canvas.width = Math.min(video.videoWidth, 960);
              canvas.height = Math.round(canvas.width * (video.videoHeight / video.videoWidth));
              const ctx = canvas.getContext('2d');
              if (ctx) {
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                const frame = canvas.toDataURL('image/jpeg', 0.55);
                userSendSignal(sessionToJoin.id, { type: 'screen_frame', frame, timestamp: Date.now() }).catch(() => {});
              }
            };
            setTimeout(sendScreenFrame, 300);
            screenIntervalRef.current = setInterval(sendScreenFrame, 1800);
          } catch (streamErr) {
            console.warn('Screen sharing cancelled by user', streamErr);
            stopAllStreams();
            await respondToSession(sessionToJoin.id, false).catch(() => {});
            setActiveSessionInfo(null);
          }
        }
      } else {
        stopAllStreams();
        setActiveSessionInfo(null);
      }
    } catch (err) {
      console.error('Failed to respond to session:', err);
    } finally {
      setRespondingToSession(false);
    }
  };

  // ── Render a single nav item (link or nested subitems)
  const renderNavItem = (item) => {
    const Icon = item.icon;
    if (item.subItems) {
      const isSubExpanded = activeAccordion === item.name;
      const hasActiveChild = item.subItems.some((sub) => isActive(sub.path));
      return (
        <div key={item.name} className="nav-group-nested" style={{ margin: '2px 0' }}>
          <div className={`nav-parent ${hasActiveChild ? 'active-parent' : ''} ${isSubExpanded ? 'expanded' : ''}`}>
            <button
              type="button"
              className="nav-item nav-parent-trigger"
              onClick={() => toggleAccordion(item.name)}
              aria-expanded={isSubExpanded}
              title={item.name}
            >
              <Icon size={17} />
              <span>{item.name}</span>
              <ChevronRight className="nav-chevron" size={16} />
            </button>
            <div className="nav-subitems">
              {item.subItems.map((sub) => {
                const SubIcon = sub.icon;
                return (
                  <Link
                    key={sub.path}
                    to={sub.path}
                    onClick={() => setMobileOpen(false)}
                    className={`nav-item nav-subitem ${isActive(sub.path) ? 'active' : ''}`}
                    title={sub.name}
                  >
                    <SubIcon size={15} />
                    <span>{sub.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setMobileOpen(false)}
        className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
        title={item.name}
      >
        <Icon size={17} />
        <span>{item.name}</span>
      </Link>
    );
  };


  // ── Render a collapsible group (Teams, Security, Settings, Administration)
  const renderCollapsibleGroup = (group) => {
    const GroupIcon = group.icon;
    const expanded = activeAccordion === group.label;
    const active = group.items.some((item) => isActive(item.path));

    return (
      <div key={group.label} className="nav-group">
        <div className={`nav-parent ${active ? 'active-parent' : ''} ${expanded ? 'expanded' : ''}`}>
          <button
            type="button"
            className="nav-item nav-parent-trigger"
            onClick={() => toggleAccordion(group.label)}
            aria-expanded={expanded}
            title={group.label}
          >
            <GroupIcon size={17} />
            <span>{group.label}</span>
            <ChevronRight className="nav-chevron" size={16} />
          </button>
          <div className="nav-subitems">
            {group.items.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={`nav-item nav-subitem ${isActive(item.path) ? 'active' : ''}`}
                  title={item.name}
                >
                  <Icon size={15} />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ── Render the Vault collapsible item (special — uses vaultSubItems)
  const renderVaultItem = () => {
    const isVaultExpanded = activeAccordion === 'Vault';
    return (
      <div
        key="/vault"
        className={`nav-parent ${vaultRouteActive ? 'active-parent' : ''} ${isVaultExpanded ? 'expanded' : ''}`}
      >
        <button
          type="button"
          className="nav-item nav-parent-trigger"
          onClick={() => toggleAccordion('Vault')}
          aria-expanded={isVaultExpanded}
          title="Vault"
        >
          <LockKeyhole size={17} />
          <span>Vault</span>
          <ChevronRight className="nav-chevron" size={16} />
        </button>
        <div className="nav-subitems">
          {vaultSubItems.map((vaultItem) => {
            const VaultIcon = vaultItem.icon;
            return (
              <Link
                key={vaultItem.path}
                to={vaultItem.path}
                onClick={() => setMobileOpen(false)}
                className={`nav-item nav-subitem ${isActive(vaultItem.path) ? 'active' : ''}`}
                title={vaultItem.name}
              >
                <VaultIcon size={15} />
                <span>{vaultItem.name}</span>
              </Link>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Render a full group (section label + items)
  const renderGroup = (group) => {
    // Collapsible groups (Teams, Security, Settings, Administration)
    if (group.collapsible) {
      return renderCollapsibleGroup(group);
    }

    // Non-collapsible section (Workspace, etc.)
    return (
      <div className="nav-group" key={group.label}>
        <div className="nav-section-label">{group.label}</div>
        {group.items.map((item) => {
          if (item.isVault) return renderVaultItem();
          return renderNavItem(item);
        })}
      </div>
    );
  };

  return (
    <div
      className={`app-layout ${collapsed ? 'sidebar-collapsed' : ''} ${mobileOpen ? 'mobile-drawer-open' : ''} ${location.pathname.startsWith('/messages') ? 'app-layout-messages' : ''} ${hideSidebar ? 'app-layout-no-sidebar' : ''}`}
    >
      {mobileOpen && (
        <button
          className="mobile-menu-backdrop"
          onClick={() => setMobileOpen(false)}
          aria-label="Close navigation"
        />
      )}

      {/* ── SIDEBAR ── */}
      {!hideSidebar && (
        <aside className={`sidebar glass-panel ${mobileOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-logo">
            <Shield size={24} />
            <span>CipherVault</span>
            <button
              className="mobile-close"
              onClick={() => setMobileOpen(false)}
              aria-label="Close navigation"
            >
              <X size={20} />
            </button>
          </div>

        {/* Role badge for admin */}
        {isAdmin && (
          <div className="sidebar-role-badge">
            <ShieldCheck size={13} />
            <span>{user?.role === 'SUPER_ADMIN' ? 'Super Admin' : 'Admin'}</span>
          </div>
        )}

        <nav className="nav-items">{navGroups.map(renderGroup)}</nav>

        {/* Sidebar Bottom Messenger */}
        <div className="sidebar-bottom-messenger">
          <Link
            to="/messages"
            className={`nav-item sidebar-messenger-btn ${location.pathname.startsWith('/messages') ? 'active' : ''}`}
            title="CipherChat Messenger"
            onClick={() => setMobileOpen(false)}
          >
            <div className="messenger-icon-wrap" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <MessageSquare size={18} />
              <span className="messenger-unread-dot" />
            </div>
            {!collapsed && <span className="nav-item-label">Messenger</span>}
            {!collapsed && <span className="sidebar-messenger-badge">Chat</span>}
          </Link>
        </div>

        {/* <div className="sidebar-footer">
          <span>Zero-knowledge by design</span>
          <ChevronDown size={14} />
        </div> */}
        </aside>
      )}

      {/* ── MAIN CONTENT ── */}
      <main className={`main-content ${location.pathname.startsWith('/messages') ? 'main-content-messages' : ''}`}>
        {!location.pathname.startsWith('/messages') && (
        <div className={`topbar new-topbar ${isSearchOpen ? 'topbar-search-open' : ''}`}>
          {!hideSidebar && (
            <button
              className="mobile-menu-toggle"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation"
            >
              <Menu size={20} />
            </button>
          )}
          {!hideSidebar && (
            <button
              className="sidebar-collapse-toggle"
              onClick={toggleCollapsed}
              aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
              title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            >
              {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            </button>
          )}

          {/* Search: icon-only by default, expands to full form when clicked */}
          {isSearchOpen ? (
            <form
              className="topbar-search-expanded-form"
              onSubmit={handleSearch}
              role="search"
              autoComplete="off"
            >
              <Search size={17} className="search-icon-inside" />
              <input
                type="search"
                name="global-vault-search"
                id="global-vault-search"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                data-lpignore="true"
                data-1p-ignore="true"
                data-bwignore="true"
                data-form-type="other"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search vault..."
                aria-label="Search vault"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setIsSearchOpen(false);
                }}
              />
              <button
                type="button"
                className="topbar-search-close-btn"
                onClick={() => {
                  setSearchQuery('');
                  setIsSearchOpen(false);
                }}
                aria-label="Close search"
                title="Close search"
              >
                <X size={16} />
              </button>
            </form>
          ) : (
            <button
              type="button"
              className="topbar-icon-button topbar-search-trigger-btn desktop-search-btn hidden-on-mobile"
              onClick={() => setIsSearchOpen(true)}
              aria-label="Search vault"
              title="Search vault"
            >
              <Search size={18} />
            </button>
          )}

          <div className="topbar-actions new-topbar-actions">
            <PinnedAppsHeader />
            <AppLauncherDropdown />

            {/* Topbar Messages Icon (Hidden on mobile width) */}
            <Link
              className={`topbar-icon-button topbar-messages-btn hidden-on-mobile ${location.pathname.startsWith('/messages') ? 'active' : ''}`}
              to="/messages"
              aria-label="Open Messages"
              title="CipherChat Messenger"
            >
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <MessageSquare size={19} />
                <span
                  className="messenger-topbar-dot"
                  style={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    width: 8,
                    height: 8,
                    background: '#10b981',
                    borderRadius: '50%',
                    border: '1.5px solid var(--bg-card, #fff)',
                  }}
                />
              </div>
            </Link>

            <Link
              className="topbar-icon-button"
              to="/security"
              aria-label="Open security notifications"
              title="Security notifications"
            >
              <div style={{ position: 'relative' }}>
                <Bell size={20} />
                <span
                  className="notification-dot"
                  style={{
                    position: 'absolute',
                    top: -2,
                    right: 0,
                    width: 8,
                    height: 8,
                    background: 'var(--danger)',
                    borderRadius: '50%',
                  }}
                />
              </div>
            </Link>

            <div
              className="theme-switcher hidden-on-mobile"
              role="group"
              aria-label="Theme preference"
            >
              <button
                className={theme === 'light' ? 'theme-option active' : 'theme-option'}
                onClick={() => setTheme('light')}
                aria-label="Use light theme"
                title="Light theme"
              >
                <Sun size={15} />
              </button>
              <button
                className={theme === 'system' ? 'theme-option active' : 'theme-option'}
                onClick={() => setTheme('system')}
                aria-label="Use system theme"
                title="System theme"
              >
                <Monitor size={15} />
              </button>
              <button
                className={theme === 'dark' ? 'theme-option active' : 'theme-option'}
                onClick={() => setTheme('dark')}
                aria-label="Use dark theme"
                title="Dark theme"
              >
                <Moon size={15} />
              </button>
            </div>

            {/* Topbar Add Login Button (Hidden on mobile width) */}
            <button
              type="button"
              className="topbar-add-login-btn hidden-on-mobile"
              onClick={() => setIsAddAccountOpen(true)}
              title="Add another account / Login"
              aria-label="Add account"
            >
              <UserPlus size={14} />
              <span className="topbar-add-login-text">Add Login</span>
            </button>

            <div className="profile-menu">
              <button
                type="button"
                className="profile-icon-only-btn"
                onClick={() => setProfileOpen((current) => !current)}
                aria-expanded={profileOpen}
                aria-label="Account Profile & Switcher"
                title={user?.email || 'Profile'}
              >
                <div className="profile-avatar-circle-wrapper">
                  <img
                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || user?.email || 'User')}&background=1e293b&color=38bdf8&bold=true`}
                    alt="Profile avatar"
                    className="profile-avatar-img"
                  />
                  {savedAccounts.length > 1 && (
                    <span className="profile-accounts-count-pill" title={`${savedAccounts.length} accounts saved`}>
                      {savedAccounts.length}
                    </span>
                  )}
                </div>
              </button>

              {profileOpen && renderProfileMenu()}
            </div>
          </div>
        </div>
        )}

        {children}

        {/* Active Session Notification Pill */}
        {activeSessionInfo && (
          <div style={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 9998,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            background: 'rgba(239, 68, 68, 0.94)',
            backdropFilter: 'blur(10px)',
            color: '#fff',
            padding: '10px 18px',
            borderRadius: 30,
            boxShadow: '0 8px 32px rgba(239,68,68,0.4)',
            fontSize: '0.84rem',
            fontWeight: 600,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', display: 'inline-block' }} />
            <span>Active Admin {activeSessionInfo.session_type?.replace('_', ' ')} Session</span>
            <button
              onClick={() => {
                stopAllStreams();
                respondToSession(activeSessionInfo.id, false).catch(() => {});
                setActiveSessionInfo(null);
              }}
              style={{
                background: '#fff',
                color: '#ef4444',
                border: 'none',
                borderRadius: 20,
                padding: '4px 12px',
                fontSize: '0.76rem',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              End Session
            </button>
          </div>
        )}

        {/* Pending Admin Screen Share Session Request Modal */}
        {pendingSession && pendingSession.session_type === 'screen_share' && (
          <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}>
            <div style={{
              background: 'var(--bg-card, #1e293b)',
              border: '1px solid rgba(6, 182, 212, 0.35)',
              borderRadius: 20,
              padding: 28,
              maxWidth: 440,
              width: '100%',
              boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
              textAlign: 'center',
            }}>
              <div style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'rgba(6, 182, 212, 0.15)',
                color: '#06b6d4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <Monitor size={28} />
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                Screen Share Request
              </h3>
              <p style={{ margin: '0 0 20px', color: 'var(--text-secondary)', fontSize: '0.88rem', lineHeight: 1.5 }}>
                An administrator is requesting real-time screen share access to your device. You will choose which window or screen to share. You may terminate the session at any time.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  disabled={respondingToSession}
                  onClick={() => handleRespondSession(false)}
                  style={{
                    flex: 1,
                    height: 42,
                    borderRadius: 10,
                    background: 'rgba(239, 68, 68, 0.1)',
                    color: '#ef4444',
                    border: '1px solid rgba(239, 68, 68, 0.25)',
                    fontWeight: 600,
                    fontSize: '0.88rem',
                    cursor: 'pointer',
                  }}
                >
                  Decline
                </button>
                <button
                  type="button"
                  disabled={respondingToSession}
                  onClick={() => handleRespondSession(true)}
                  style={{
                    flex: 1.5,
                    height: 42,
                    borderRadius: 10,
                    background: 'linear-gradient(135deg, #06b6d4, #0284c7)',
                    color: '#fff',
                    border: 'none',
                    fontWeight: 600,
                    fontSize: '0.88rem',
                    cursor: 'pointer',
                  }}
                >
                  {respondingToSession ? 'Connecting...' : 'Accept & Share'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Device Consent Modal */}
        {showConsentModal && (
          <DeviceConsentModal
            isFirstTime={isFirstConsent}
            onComplete={() => {
              if (user?.id) {
                window.localStorage.setItem(`ciphervault_consent_completed_${user.id}`, '1');
              }
              window.localStorage.setItem('ciphervault_consent_completed_global', '1');
              setShowConsentModal(false);
            }}
            onDismiss={() => {
              if (user?.id) {
                window.localStorage.setItem(`ciphervault_consent_completed_${user.id}`, '1');
              }
              window.localStorage.setItem('ciphervault_consent_completed_global', '1');
              setShowConsentModal(false);
            }}
          />
        )}

        {/* Toast Notification Banner */}
        {switchToast && (
          <div className="ios-toast-banner">
            <CheckCircle2 size={18} color="#10b981" />
            <span>{switchToast}</span>
          </div>
        )}

        {/* Multi-Account Modals */}
        <AddAccountModal
          isOpen={isAddAccountOpen}
          onClose={() => setIsAddAccountOpen(false)}
          onAccountAdded={(newAc) => {
            const acName = newAc?.name || newAc?.email?.split('@')[0] || newAc?.email || 'Account';
            setSwitchToast(`Added & switched to ${acName}`);
            setTimeout(() => setSwitchToast(null), 3200);
          }}
        />

        <SwitchAccount2FAModal
          isOpen={!!accountPending2FA}
          targetAccount={accountPending2FA}
          onClose={() => setAccountPending2FA(null)}
          onVerified={handle2FASwitchSuccess}
        />

        <ReauthAccountModal
          isOpen={!!accountPendingReauth}
          targetAccount={accountPendingReauth}
          onClose={() => setAccountPendingReauth(null)}
          onSuccess={(account) => {
            setAccountPendingReauth(null);
            const acName = account?.name || account?.email?.split('@')[0] || account?.email || 'Account';
            setSwitchToast(`Switched to ${acName}`);
            setTimeout(() => setSwitchToast(null), 3200);
          }}
        />

        <MobileInstallModal isOpen={mobileModalOpen} onClose={() => setMobileModalOpen(false)} />
        <MobileBottomNav
          hideDock={Boolean(hideDockProp || location.pathname.startsWith('/messages'))}
          isSidebarOpen={mobileOpen}
          isModalOpen={profileOpen || isAddAccountOpen || Boolean(accountPending2FA) || Boolean(accountPendingReauth) || mobileModalOpen || showConsentModal}
          onOpenMobileInstall={() => setMobileModalOpen(true)}
        />
      </main>
    </div>
  );
};

export default MainLayout;
